import { useCallback, useEffect, useState } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { supabase } from '@/integrations/supabase/client';
import { isNativeApp } from '@/lib/nativeApp';

// Bridge until Capgo OTA (CLAUDE.md §24). Users who sideloaded the direct
// download APK (§25) have no update channel — this hook compares the installed
// versionCode against the latest CI-built debug APK recorded in
// public.app_version_info and reports whether a newer build exists.
//
// NATIVE ONLY. On web every value stays inert (updateAvailable === false), so
// nothing renders and no query is issued.
//
// CHANNEL GUARD (2026-07-28). app_version_info tracks the DEBUG/sideload
// channel only — android-debug-build.yml is its sole writer, and the banner's
// Download button hands out the sideload APK. That workflow now stamps each
// debug build with a versionCode in a 1000000+ band so successive sideload
// builds actually compare as newer (before this, every debug APK shared one
// static code and the banner could never fire — the bug this guard accompanies).
//
// A side effect of that band is that the recorded code is ALWAYS greater than
// any Play release code, so a bare "latest > installed" test would show every
// Play Store user a permanent banner pointing at an unsigned sideload build.
// The earlier "no install-source detection needed" note was only safe while the
// two channels shared a versionCode; it no longer is. We therefore key off the
// installed versionName, which the debug workflow suffixes with `-debug.<run>`
// and the release workflow never does. No install-source API required.

export const APK_DOWNLOAD_URL =
  'https://hfohypmddocxhgixgbpu.supabase.co/storage/v1/object/public/app-downloads/behtari-latest.apk';

interface AppVersionInfo {
  version_code: number;
  version_name: string;
  apk_url: string | null;
}

// Session-level cache: one read per app session, refreshed on resume at most
// every REFETCH_AFTER_MS so a build shipped while backgrounded is picked up.
const REFETCH_AFTER_MS = 30 * 60 * 1000;
let cachedLatest: AppVersionInfo | null = null;
let cachedAt = 0;
let inFlight: Promise<AppVersionInfo | null> | null = null;

async function fetchLatestVersion(force = false): Promise<AppVersionInfo | null> {
  const fresh = cachedLatest && Date.now() - cachedAt < REFETCH_AFTER_MS;
  if (!force && fresh) return cachedLatest;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      // app_version_info is not in the generated Supabase types yet (the table
      // is created by migration 20260727090000; types.ts is regenerated by
      // Lovable and is a locked artifact). Same localized-cast pattern as
      // anomaly_resolutions.
      const { data, error } = await (supabase as any)
        .from('app_version_info')
        .select('version_code, version_name, apk_url')
        .maybeSingle();

      if (error || !data) return cachedLatest;

      cachedLatest = {
        version_code: Number(data.version_code),
        version_name: String(data.version_name ?? ''),
        apk_url: data.apk_url ?? null,
      };
      cachedAt = Date.now();
      return cachedLatest;
    } catch {
      // Never let a version check surface as an error to the user.
      return cachedLatest;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export interface AppUpdateState {
  updateAvailable: boolean;
  latestVersionName: string | null;
  latestVersionCode: number | null;
  installedVersionCode: number | null;
  apkUrl: string;
}

/** Marker stamped into versionName by android-debug-build.yml only. */
const DEBUG_CHANNEL_MARKER = '-debug.';

export function useAppUpdateAvailable(): AppUpdateState {
  const [installed, setInstalled] = useState<number | null>(null);
  const [isSideloadBuild, setIsSideloadBuild] = useState(false);
  const [latest, setLatest] = useState<AppVersionInfo | null>(null);

  // Installed versionCode — Android reports it as App.getInfo().build, and the
  // versionName as App.getInfo().version.
  useEffect(() => {
    if (!isNativeApp()) return;
    let cancelled = false;

    CapacitorApp.getInfo()
      .then((info) => {
        if (cancelled) return;
        const build = Number.parseInt(String(info?.build ?? ''), 10);
        if (Number.isInteger(build) && build > 0) setInstalled(build);
        setIsSideloadBuild(String(info?.version ?? '').includes(DEBUG_CHANNEL_MARKER));
      })
      .catch(() => { /* plugin unavailable — stay inert */ });

    return () => { cancelled = true; };
  }, []);

  const refresh = useCallback((force = false) => {
    if (!isNativeApp()) return;
    fetchLatestVersion(force).then((info) => {
      if (info) setLatest(info);
    });
  }, []);

  useEffect(() => {
    if (!isNativeApp()) return;
    refresh(false);

    // nativeRealtimeLifecycle emits this on app resume (no-op on web).
    const onResume = () => refresh(false);
    window.addEventListener('behtari:app-resumed', onResume);
    return () => window.removeEventListener('behtari:app-resumed', onResume);
  }, [refresh]);

  const updateAvailable =
    isNativeApp() &&
    // Sideload builds only — a Play Store install is updated by Google and must
    // never be pointed at the unsigned debug APK.
    isSideloadBuild &&
    installed !== null &&
    latest !== null &&
    Number.isInteger(latest.version_code) &&
    latest.version_code > installed;

  return {
    updateAvailable,
    latestVersionName: latest?.version_name ?? null,
    latestVersionCode: latest?.version_code ?? null,
    installedVersionCode: installed,
    apkUrl: latest?.apk_url || APK_DOWNLOAD_URL,
  };
}
